import { atom, useAtomValue } from 'jotai';
import request from 'lib/request';
import { useMemo, useState, useEffect } from 'react';
import { Anchor, Popup, useMap } from 'react-map-gl';
import { SearchResult, WellsResponse } from 'types/api-responses';
import { wellStatusLabel } from 'utils/well-status';
import { CubeView } from '@carbon/react/icons';
import { parseUTCdateTime } from 'utils/dates';
import style from './map.module.scss';
import { resourcesAtom } from 'atoms/root';
import { Link } from 'react-router-dom';
import { Button, Loading } from '@carbon/react';
import { EMPTY_CELL, LinkCell } from 'components/link-cell';
import { formatLeaseAndWell } from 'utils/well-name';
import Well3DViewer from '../well-3d-viewer/well-3d-viewer';

type WellsMap = Record<
  string,
  | {
      uwi: string;
      name: string | null;
      status: string | null;
      leaseName: string | null;
      wellNumber: string | null;
      currentOperator: string | null;
      currentOperatorHref: string | null;
      srpLandingZone: string | null;
      /** Landing-analysis Landing Zone for the requested collection. Null when the
       *  map has no collection in scope, or the well has no analysis for it. */
      landingZone: string | null;
      /** SRP code resolved to its geologic interval name. */
      srpLandingZoneLabel: string | null;
      permitDate: string | null;
      spudDate: string | null;
      completionDate: string | null;
      firstProductionDate: string | null;
      lastProductionDate: string | null;
    }
  | undefined
>;

const createWellSearchAtom = (
  wells: WellsMap,
  queryHref: string | undefined,
  structureGridCollectionId: number | null | undefined
) => {
  const abortableAtom = atom<{ loading: boolean; wells: WellsMap }>({
    loading: false,
    wells,
  });

  abortableAtom.onMount = (setAtom) => {
    // Nothing to fetch without the endpoint — bail before scheduling anything,
    // so the debounce timer never flips the spinner on (it early-returns and
    // never clears it). queryHref is a const, so TS still narrows it to a
    // string inside queryWells below.
    if (!queryHref) return;

    const abortController = new AbortController();

    const queryWells = async () => {
      const wellsCopy = { ...wells };
      const promises = Object.keys(wellsCopy).map((el) =>
        request
          .post<
            SearchResult<WellsResponse>,
            {
              uwiIds: string[];
              responseConfig: { includeFacets: false };
              useElastic: true;
              includeLandingZone: true;
              structureGridCollectionId?: number;
            }
          >(
            queryHref,
            {
              // We already know the exact UWI, so look it up with the exact
              // terms filter rather than `uwi`, which runs a leading-wildcard
              // (`*uwi*`) ES query that cannot seek the term dictionary and
              // scans every uwi term across the whole index. One request fires
              // per well under the cursor, so that cost is paid per hovered well.
              uwiIds: [el],
              // This is a point lookup, not a search: opt out of the facet
              // aggregations (same response_config shape the documents query
              // uses). Without it the backend still runs the uwi facet over the
              // whole uwi dictionary — the same cost in a different place.
              responseConfig: { includeFacets: false },
              // uwi_ids is an Elastic-only filter; force the Elastic backend
              // explicitly rather than relying on includeLandingZone to do it.
              useElastic: true,
              // Opts into the landing-analysis Landing Zone and the resolved
              // SRP label. Sent on every request so the SRP code is never
              // rendered raw; the collection is only sent when the page has one.
              includeLandingZone: true,
              ...(typeof structureGridCollectionId === 'number'
                ? { structureGridCollectionId }
                : {}),
            },
            abortController
          )
          .then((response) => {
            if (response.type === 'success') {
              const { results } = response.data;
              if (results.length) {
                const wellInfo = results[0];
                wellsCopy[wellInfo.uwi] = {
                  uwi: wellInfo.uwi,
                  name: wellInfo.name || null,
                  status: wellInfo.status || null,
                  leaseName: wellInfo.leaseName || null,
                  wellNumber: wellInfo.wellNumber || null,
                  currentOperator: wellInfo.currentOperator || null,
                  currentOperatorHref: wellInfo.currentOperatorHref || null,
                  srpLandingZone: wellInfo.srpLandingZone || null,
                  landingZone: wellInfo.landingZone || null,
                  srpLandingZoneLabel: wellInfo.srpLandingZoneLabel || null,
                  permitDate: wellInfo.permitDate || null,
                  spudDate: wellInfo.spudDate || null,
                  completionDate: wellInfo.completionDate || null,
                  firstProductionDate: wellInfo.firstProductionDate || null,
                  lastProductionDate: wellInfo.lastProductionDate || null,
                };
              }
            }
          })
      );

      // allSettled, not all().catch(): the bare .catch() had no handler, so a
      // throw inside any .then (e.g. an unexpected response shape) rejected
      // Promise.all and skipped the loading:false setAtom below, leaving the
      // popup stuck on its spinner.
      const settled = await Promise.allSettled(promises);
      // Surface a rejected lookup (e.g. an unexpected response shape) instead of
      // silently showing a bare UWI with nothing to debug from.
      settled.forEach((result) => {
        if (result.status === 'rejected') {
          console.error('well-popup: wells lookup failed', result.reason);
        }
      });
      // Skip the state write if the popup was torn down mid-flight (abort).
      if (!abortController.signal.aborted) {
        setAtom(() => ({ loading: false, wells: wellsCopy }));
      }
    };

    // Wait for the user to settle in the mouse position before
    // triggering the wells queries as the well density is usually high.
    const timeout = setTimeout(() => {
      setAtom((current) => ({ loading: true, wells: { ...current.wells } }));
      queryWells();
    }, 500);

    return () => {
      clearTimeout(timeout);
      abortController.abort();
    };
  };
  return abortableAtom;
};

const WellPopup = ({
  wells,
  latitude,
  longitude,
  closeButton,
  onClose,
  structureGridCollectionId,
}: {
  wells: Record<string, undefined>;
  latitude: number;
  longitude: number;
  closeButton: boolean;
  onClose: () => void;
  structureGridCollectionId?: number | null;
}) => {
  const resources = useAtomValue(resourcesAtom);
  const queryHref = resources?.queries?.['wells']?.href;

  const wellsFetcherAtom = useMemo(
    () => createWellSearchAtom(wells, queryHref, structureGridCollectionId),
    [wells, queryHref, structureGridCollectionId]
  );
  const wellsFetcher = useAtomValue(wellsFetcherAtom);

  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [selectedUwi, setSelectedUwi] = useState<string | undefined>();

  const { current: map } = useMap();
  const [anchor, setAnchor] = useState<Anchor>('right');

  useEffect(() => {
    if (!map) return;

    const mapContainer = map.getContainer();
    const { width, height } = mapContainer.getBoundingClientRect();
    const point = map.project([longitude, latitude]);
    const popupWidth = 400;
    const popupHeight = 200;
    const buffer = 10;
    const spaceOnRight = width - point.x - buffer;
    const spaceOnBottom = height - point.y - buffer;

    let horizontalAnchor: string;
    if (spaceOnRight < popupWidth) {
      horizontalAnchor = 'right';
    } else {
      horizontalAnchor = 'left';
    }

    let verticalAnchor: string;
    if (spaceOnBottom < popupHeight) {
      verticalAnchor = 'bottom';
    } else {
      verticalAnchor = 'top';
    }

    const finalAnchor: Anchor = verticalAnchor
      ? (`${verticalAnchor}-${horizontalAnchor}` as Anchor)
      : (horizontalAnchor as Anchor);

    setAnchor(finalAnchor);
  }, [map, longitude, latitude]);

  const handleOpen3DViewer = (uwi: string) => {
    setSelectedUwi(uwi);
    setIsViewerOpen(true);
  };

  const handleClose3DViewer = () => {
    setIsViewerOpen(false);
    setSelectedUwi(undefined);
  };

  const formatValue = (value: string | null): string => {
    return value || EMPTY_CELL;
  };

  const formatDate = (date: string | null): string => {
    return date ? parseUTCdateTime(date) : EMPTY_CELL;
  };

  return (
    <>
      <Popup
        key={Number(closeButton)}
        closeButton={closeButton}
        closeOnClick={false}
        onClose={onClose}
        latitude={latitude}
        longitude={longitude}
        className={`${style.popup} ${style.wellPopup}`}
        maxWidth="none"
        anchor={anchor}
      >
        {wellsFetcher.loading ? (
          <div className={style.wellPopupLoading}>
            <Loading small />
          </div>
        ) : (
          <div className={style.wellPopupExpanded}>
            {Object.entries(wellsFetcher.wells).map(([key, data]) => (
              <div key={key} className={style.wellPopupItem}>
                {data ? (
                  <>
                    {/* Well UWI Header with inline 3D button */}
                    <div className={style.wellHeader}>
                      <p>
                        <Link
                          to={`/wells/${data.uwi}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {data.uwi}
                        </Link>
                      </p>
                      <div className={style.wellActions}>
                        <Button
                          size="sm"
                          renderIcon={CubeView}
                          onClick={() => handleOpen3DViewer(data.uwi)}
                          className={style.viewWellButton}
                        >
                          View in 3D
                        </Button>
                      </div>
                    </div>

                    {/* Two Column Layout */}
                    <div className={style.wellColumns}>
                      {/* First Column */}
                      <div className={style.wellColumn}>
                        <div className={style.wellField}>
                          <span className={style.fieldValue}>
                            {formatLeaseAndWell(
                              data.leaseName,
                              data.wellNumber
                            ) ?? EMPTY_CELL}
                          </span>
                        </div>

                        {/* Unlabeled on screen, like the lease row; named for assistive tech. */}
                        <div
                          className={style.wellField}
                          role="group"
                          aria-label="Operator"
                        >
                          <span className={style.fieldValue}>
                            <LinkCell
                              value={data.currentOperator}
                              href={data.currentOperatorHref}
                            />
                          </span>
                        </div>

                        <div className={style.wellField}>
                          <span className={style.fieldText}>
                            <span className={style.fieldLabel}>
                              Landing Zone:
                            </span>
                            <span className={style.fieldValue}>
                              {/* The landing-analysis value when the page is
                                  scoped to a collection, otherwise the vendor
                                  value resolved to its geologic interval name.
                                  Raw srpLandingZone is the last resort so an
                                  unmapped code still shows something. */}
                              {formatValue(
                                data.landingZone ||
                                  data.srpLandingZoneLabel ||
                                  data.srpLandingZone
                              )}
                            </span>
                          </span>
                        </div>

                        <div className={style.wellField}>
                          <span className={style.fieldText}>
                            <span className={style.fieldLabel}>Status:</span>
                            <span className={style.fieldValue}>
                              {wellStatusLabel(data.status) || EMPTY_CELL}
                            </span>
                          </span>
                        </div>
                      </div>

                      {/* Second Column */}
                      <div className={style.wellColumn}>
                        <div className={style.wellField}>
                          <span className={style.fieldText}>
                            <span className={style.fieldLabel}>
                              Permit Date:
                            </span>
                            <span className={style.fieldValue}>
                              {formatDate(data.permitDate)}
                            </span>
                          </span>
                        </div>

                        <div className={style.wellField}>
                          <span className={style.fieldText}>
                            <span className={style.fieldLabel}>Spud Date:</span>
                            <span className={style.fieldValue}>
                              {formatDate(data.spudDate)}
                            </span>
                          </span>
                        </div>

                        <div className={style.wellField}>
                          <span className={style.fieldText}>
                            <span className={style.fieldLabel}>
                              Completion Date:
                            </span>
                            <span className={style.fieldValue}>
                              {formatDate(data.completionDate)}
                            </span>
                          </span>
                        </div>

                        <div className={style.wellField}>
                          <span className={style.fieldText}>
                            <span className={style.fieldLabel}>
                              First Production:
                            </span>
                            <span className={style.fieldValue}>
                              {formatDate(data.firstProductionDate)}
                            </span>
                          </span>
                        </div>

                        <div className={style.wellField}>
                          <span className={style.fieldText}>
                            <span className={style.fieldLabel}>
                              Last Production:
                            </span>
                            <span className={style.fieldValue}>
                              {formatDate(data.lastProductionDate)}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className={style.wellPopupInitial}>
                    <p>{key}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Popup>
      {/* Render the 3D viewer component */}
      <Well3DViewer
        isOpen={isViewerOpen}
        onClose={handleClose3DViewer}
        latitude={latitude}
        longitude={longitude}
        uwi={selectedUwi}
      />
    </>
  );
};

export { WellPopup };
